import StarterIcon from '../assets/images/comniqAI/free.png';
import GrowthIcon from '../assets/images/comniqAI/Growth.png';
import ScaleIcon from '../assets/images/comniqAI/Scale.png';
import ProIcon from '../assets/images/comniqAI/Pro.png';
import EnterpriseIcon from '../assets/images/comniqAI/Enterprise.png';
import { motion, AnimatePresence } from 'framer-motion';

export const formatText = (text) => {
  return text.split(/\*\*(.*?)\*\*/g).map((part, index) => (index % 2 === 1 ? <strong key={index}>{part}</strong> : part));
};

// ---------------- Plan Levels ----------------
export const PLAN_LEVELS = {
  Free: 0,
  Starter: 1,
  Growth: 2,
  Scale: 3,
  Pro: 4,
  Enterprise: 5,
};

export const getPlanLevel = (planName) => PLAN_LEVELS[planName] ?? PLAN_LEVELS.Free;

export const planIcons = {
  Starter: <img src={StarterIcon} alt='Starter Plan' />,
  Growth: <img src={GrowthIcon} alt='Growth Plan' />,
  Scale: <img src={ScaleIcon} alt='Scale Plan' />,
  Pro: <img src={ProIcon} alt='Pro Plan' />,
  Enterprise: <img src={EnterpriseIcon} alt='Enterprise Plan' />,
};

export const getPlanAction = (product, subscriptionDetails) => {
  const currentSubscription = subscriptionDetails?.user?.subscription;

  const currentPlanName = currentSubscription?.name ?? 'Free';
  const currentPlanInterval = currentSubscription?.interval;
  const currentPlanLevel = getPlanLevel(currentPlanName);

  const targetPlanName = product?.name ?? 'Free';
  const targetPlanInterval = product?.interval;
  const targetPlanLevel = getPlanLevel(targetPlanName);

  // Custom button text override
  if (product?.customActionText) return product.customActionText;

  if (!currentSubscription && targetPlanName?.toLowerCase() === 'starter' && product?.price === 0) {
    return 'Active';
  }

  // If current plan is yearly, all monthly plans should show Get Started
  if (currentPlanInterval === 'year' && targetPlanInterval === 'month') {
    return 'Get Started';
  }

  // Check if this is the exact same plan (same name AND same interval)
  const isSamePlan = currentPlanName === targetPlanName && currentPlanInterval === targetPlanInterval;
  if (isSamePlan) {
    return 'Active';
  }

  // Prevent showing Downgrade for Free target plan
  if (targetPlanLevel === PLAN_LEVELS.Starter) {
    return 'Get Started';
  }

  if (
    currentPlanInterval === 'month' &&
    targetPlanInterval === 'month' &&
    targetPlanLevel < currentPlanLevel // Only lower plans
  ) {
    return 'Downgrade';
  }

  // Higher level plans - always show Upgrade
  if (targetPlanLevel > currentPlanLevel) {
    return 'Upgrade';
  }

  // Same level but different interval - show Upgrade
  if (targetPlanLevel === currentPlanLevel && currentPlanInterval !== targetPlanInterval) {
    return 'Upgrade';
  }

  // Lower level plans - disable them
  if (targetPlanLevel < currentPlanLevel && currentPlanInterval !== targetPlanInterval) {
    return 'Upgrade'; // This will be disabled in the component
  }

  return 'Get Started';
};

export function AnimatedPrice({ price, billing, planName }) {
  if (planName === 'Starter') {
    return <span className='subscription-card-subtitle outfit-semibold'>{price}</span>;
  }

  return (
    <AnimatePresence mode='wait'>
      <motion.span
        key={billing + planName + price}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        className='subscription-card-subtitle outfit-semibold'
      >
        ${price}
      </motion.span>
    </AnimatePresence>
  );
}

export function DiscountBadge({ discount, billing, planName }) {
  if (planName === 'Starter') {
    return '';
  }
  return (
    <AnimatePresence mode='wait'>
      {discount && (
        <motion.span
          key={billing + planName + discount}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            animate: { duration: 2, ease: 'easeInOut' },
            exit: { duration: 0.3, ease: 'easeInOut' },
          }}
          className='discount-badge outfit-semibold'
        >
          {discount}% off
        </motion.span>
      )}
    </AnimatePresence>
  );
}

export function formatTimestamp(timestamp) {
  const date = new Date(timestamp);

  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  const formattedDate = date.toLocaleDateString('en-US', options);
  const formattedTime = date.toLocaleTimeString('en-US', timeOptions);

  return `${formattedDate} at ${formattedTime}`;
}

export function formatTimestampByZone(timestamp, zone = 'America/New_York') {
  const date = new Date(timestamp);

  const day = date.toLocaleString('en-US', {
    day: '2-digit',
    timeZone: zone,
  });

  const month = date.toLocaleString('en-US', {
    month: 'short',
    timeZone: zone,
  });

  const year = date.toLocaleString('en-US', {
    year: 'numeric',
    timeZone: zone,
  });

  const time = date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: zone,
  });

  return `${day}-${month}-${year}`;
}

export const formatTimeAgo = (date) => {
  if (!date) return '';
  const now = new Date();
  const seconds = Math.floor((now - new Date(date)) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;

  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
